import { useEffect, useState } from 'react';

import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';
import { useFinanceFilters, FinanceFilterBar, MKT_LABELS } from '../lib/FinanceFilters.js';

interface KPIs {
  revenue: number; netProfit: number; marginPct: number; tacos: number;
  totalFees: number; referralFee: number; fbaFee: number; storageFee: number;
  inboundFee: number; returnsFee: number; otherFees: number;
  adSpend: number; skuAdSpend: number; cogs: number;
  cm1: number; cm2: number; cm1Pct: number; cm2Pct: number; breakEvenAcos: number | null;
  units: number; refunds: number; reimbursements: number; promotions: number;
}

interface PeriodSummary { revenue: number; netProfit: number; adSpend: number; units: number }
interface SkuSummary { sku: string; revenue: number; netProfit: number; units: number }
interface MarketplaceRow { marketplace: string; revenue: number; netProfit: number; fees: number; adSpend: number }

interface OverviewData {
  period: number; kpis: KPIs;
  last7d: PeriodSummary; last30d: PeriodSummary;
  topSkus: SkuSummary[]; bottomSkus: SkuSummary[];
  marketplaceBreakdown: MarketplaceRow[];
}

interface DailyRow {
  date: string; revenue: number; cogs: number;
  referralFee: number; fbaFee: number; storageFee: number; otherFees: number;
  inboundFee: number; returnsFee: number;
  adSpend: number; netProfit: number; marginPct: number;
  refunds: number; promotions: number; reimbursements: number; units: number;
}

interface Account { id: string; name: string; is_active?: number }

export function FinanceOverview() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [dailyRows, setDailyRows] = useState<DailyRow[]>([]);
  const [trendData, setTrendData] = useState<Array<{ date: string; revenue: number; profit: number; cm1: number; cm2: number; adSpend: number; tacos: number; marginPct: number; organicSalesPct: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState('');
  const { displayName, parentNickname } = useNicknames();
  const filters = useFinanceFilters('30');

  useEffect(() => {
    (async () => {
      if (!loading) setRefreshing(true);
      setError('');
      try {
        const accts = await api.get<Account[]>('/accounts');
        const active = accts.filter(a => a.is_active !== 0);
        if (active.length === 0) { setLoading(false); return; }

        const mktParam = filters.marketplace ? `&marketplace=${filters.marketplace}` : '';
        const prodParam = filters.productParam;

        // Load products for selector (once per account set)
        if (filters.products.length === 0) {
          const prods = await Promise.all(active.map(a =>
            api.get<{ products: Array<{ parentAsin: string; title: string; children: Array<{ sku: string; asin: string; label: string }> }> }>(`/finance/products/${a.id}`).catch(() => ({ products: [] }))
          ));
          const allProducts = prods.flatMap(p => p.products);
          // Deduplicate by parentAsin
          const seen = new Set<string>();
          const unique = allProducts.filter(p => { if (seen.has(p.parentAsin)) return false; seen.add(p.parentAsin); return true; });
          filters.setProducts(unique);
        }

        // Merge data from all accounts
        const [overviews, dailyData, trends] = await Promise.all([
          Promise.all(active.map(a => api.get<OverviewData & { marketplaces?: string[] }>(`/finance/overview/${a.id}?period=${filters.period}${filters.dateParam}${mktParam}${filters.currencyParam}${prodParam}`).catch(() => null))),
          Promise.all(active.map(a => api.get<{ rows: DailyRow[] }>(`/finance/daily/${a.id}?startDate=${filters.startDate}&endDate=${filters.endDate}&limit=200${mktParam}${filters.currencyParam}${prodParam}`).catch(() => ({ rows: [] })))),
          Promise.all(active.map(a => api.get<{ series: typeof trendData }>(`/finance/trends/${a.id}?days=${filters.period}${filters.dateParam}${mktParam}${filters.currencyParam}`).catch(() => ({ series: [] })))),
        ]);

        // Merge overview data from ALL accounts (not just first)
        const merged: OverviewData = {
          period: Number(filters.period),
          kpis: { revenue: 0, netProfit: 0, marginPct: 0, tacos: 0, totalFees: 0, referralFee: 0, fbaFee: 0, storageFee: 0, inboundFee: 0, returnsFee: 0, otherFees: 0, adSpend: 0, skuAdSpend: 0, cogs: 0, cm1: 0, cm2: 0, cm1Pct: 0, cm2Pct: 0, breakEvenAcos: null, units: 0, refunds: 0, reimbursements: 0, promotions: 0 },
          last7d: { revenue: 0, netProfit: 0, adSpend: 0, units: 0 },
          last30d: { revenue: 0, netProfit: 0, adSpend: 0, units: 0 },
          topSkus: [], bottomSkus: [], marketplaceBreakdown: [],
        };
        const mktSet = new Set<string>();
        for (const o of overviews) {
          if (!o) continue;
          // Sum KPIs
          for (const k of Object.keys(merged.kpis) as (keyof KPIs)[]) {
            if (typeof merged.kpis[k] === 'number' && typeof o.kpis[k] === 'number') {
              (merged.kpis[k] as number) += o.kpis[k] as number;
            }
          }
          // Sum period summaries
          merged.last7d.revenue += o.last7d.revenue; merged.last7d.netProfit += o.last7d.netProfit;
          merged.last7d.adSpend += o.last7d.adSpend; merged.last7d.units += o.last7d.units;
          merged.last30d.revenue += o.last30d.revenue; merged.last30d.netProfit += o.last30d.netProfit;
          merged.last30d.adSpend += o.last30d.adSpend; merged.last30d.units += o.last30d.units;
          // Merge SKUs and marketplace breakdown
          merged.topSkus.push(...o.topSkus);
          merged.bottomSkus.push(...o.bottomSkus);
          merged.marketplaceBreakdown.push(...o.marketplaceBreakdown);
          // Collect marketplaces
          for (const m of (o.marketplaceBreakdown ?? [])) mktSet.add(m.marketplace);
        }
        // Recalculate derived percentages
        if (merged.kpis.revenue > 0) {
          merged.kpis.marginPct = (merged.kpis.netProfit / merged.kpis.revenue) * 100;
          merged.kpis.tacos = (merged.kpis.adSpend / merged.kpis.revenue) * 100;
          merged.kpis.cm1Pct = (merged.kpis.cm1 / merged.kpis.revenue) * 100;
          merged.kpis.cm2Pct = (merged.kpis.cm2 / merged.kpis.revenue) * 100;
          merged.kpis.breakEvenAcos = merged.kpis.cm1Pct > 0 ? merged.kpis.cm1Pct : null;
        }
        merged.topSkus.sort((a, b) => b.netProfit - a.netProfit);
        merged.topSkus = merged.topSkus.slice(0, 5);
        merged.bottomSkus.sort((a, b) => a.netProfit - b.netProfit);
        merged.bottomSkus = merged.bottomSkus.slice(0, 5);

        // Only update markets list on initial load (not when filtering)
        if (!filters.marketplace) filters.setMarkets([...mktSet].sort());

        setData(merged.kpis.revenue > 0 || merged.kpis.units > 0 ? merged : null);
        setDailyRows(dailyData.flatMap(d => d.rows));
        // Merge trend series from all accounts by date
        const trendMap = new Map<string, typeof trendData[0]>();
        for (const t of trends) {
          for (const d of (t.series ?? [])) {
            const ex = trendMap.get(d.date);
            if (ex) {
              ex.revenue += d.revenue; ex.profit += d.profit;
              ex.cm1 += d.cm1; ex.cm2 += d.cm2; ex.adSpend += d.adSpend;
              ex.tacos = ex.revenue > 0 ? (ex.adSpend / ex.revenue) * 100 : 0;
              ex.marginPct = ex.revenue > 0 ? (ex.profit / ex.revenue) * 100 : 0;
              ex.organicSalesPct = 0;
            } else {
              trendMap.set(d.date, { ...d });
            }
          }
        }
        setTrendData(Array.from(trendMap.values()).sort((a, b) => a.date.localeCompare(b.date)));
      } catch (e) { setError(String(e)); }
      setLoading(false);
      setRefreshing(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.period, filters.marketplace, filters.currency, filters.startDate, filters.endDate, filters.selectedProduct, filters.selectedSku]);

  async function handleBackfill() {
    setBackfilling(true); setBackfillMsg('');
    try {
      const accts = await api.get<Account[]>('/accounts');
      const active = accts.filter(a => a.is_active !== 0);
      for (const a of active) {
        const result = await api.post<{ message: string }>(`/finance/backfill/${a.id}`, { days: 90 });
        setBackfillMsg(prev => prev + (prev ? ' | ' : '') + `${a.name}: ${result.message}`);
      }
    } catch (e) { setBackfillMsg(String(e)); }
    setBackfilling(false);
  }

  const fmt = filters.fmt;
  const dailyAggregated = aggregateByDate(dailyRows);

  if (loading) return <LoadingSkeleton />;

  return (
    <div className={`space-y-6 ${refreshing ? 'opacity-60' : ''}`}>
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">P&L Daily <span className="text-xs font-normal text-gray-400 ml-2">All amounts in {filters.currencyLabel}</span></h1>
            <p className="mt-1 text-sm text-gray-500">Operational P&L from SP-API — posted-date basis, per SKU, per day</p>
          </div>
        </div>
        <FinanceFilterBar filters={filters} showProduct />
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {!data && !error && (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <p className="text-sm font-medium text-gray-900">No financial data yet</p>
          <button onClick={handleBackfill} disabled={backfilling}
            className="mt-4 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            {backfilling ? 'Recalculating...' : 'Recalculate P&L'}
          </button>
          {backfillMsg && <p className="mt-2 text-xs text-gray-500">{backfillMsg}</p>}
        </div>
      )}

      {data && (() => {
        const k = data.kpis;
        const grossProfit = k.revenue - k.cogs;
        // Use pre-computed CM1/CM2 from merged KPIs (percentages already recalculated during multi-account merge)
        const beAcos = k.cm1Pct > 0 ? k.cm1Pct : null;
        return (
        <>
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Contribution Margin Waterfall ({filters.startDate} – {filters.endDate})</h3>
            <div className="space-y-2">
              <WaterfallRow fmt={fmt} label="Revenue" value={k.revenue} total={k.revenue} color="blue" isTotal />
              <WaterfallRow fmt={fmt} label="COGS" value={-k.cogs} total={k.revenue} color="red" indent />
              <WaterfallRow fmt={fmt} label="Gross Profit" value={grossProfit} total={k.revenue} color="green" isSubtotal />
              <WaterfallRow fmt={fmt} label="Referral Fees" value={-k.referralFee} total={k.revenue} color="red" indent />
              <WaterfallRow fmt={fmt} label="FBA Fulfillment" value={-k.fbaFee} total={k.revenue} color="red" indent />
              {k.inboundFee > 0 && <WaterfallRow fmt={fmt} label="Inbound Placement" value={-k.inboundFee} total={k.revenue} color="red" indent />}
              <WaterfallRow fmt={fmt} label="Storage Fees" value={-k.storageFee} total={k.revenue} color="red" indent />
              {k.otherFees > 0 && <WaterfallRow fmt={fmt} label="Other Amazon Fees" value={-k.otherFees} total={k.revenue} color="red" indent />}
              <WaterfallRow fmt={fmt} label="CM1" value={k.cm1} total={k.revenue} color={k.cm1 >= 0 ? 'green' : 'red'}
                isSubtotal pct={k.cm1Pct} badge={beAcos ? `BE ACoS: ${beAcos.toFixed(0)}%` : undefined} />
              <WaterfallRow fmt={fmt} label="PPC Ad Spend" value={-k.adSpend} total={k.revenue} color="orange" indent />
              <WaterfallRow fmt={fmt} label="Promotions" value={-k.promotions} total={k.revenue} color="red" indent />
              <WaterfallRow fmt={fmt} label="Refunds" value={-k.refunds} total={k.revenue} color="red" indent />
              {k.returnsFee > 0 && <WaterfallRow fmt={fmt} label="Returns Processing" value={-k.returnsFee} total={k.revenue} color="red" indent />}
              {k.reimbursements > 0 && <WaterfallRow fmt={fmt} label="Reimbursements" value={k.reimbursements} total={k.revenue} color="green" indent />}
              <WaterfallRow fmt={fmt} label="CM2" value={k.cm2} total={k.revenue} color={k.cm2 >= 0 ? 'green' : 'red'}
                isSubtotal pct={k.cm2Pct} />
            </div>
          </div>

          {/* Trend Chart — Revenue & Profit over time */}
          {trendData.length > 1 && (() => {
            const W = 800, H = 160, PAD = 4;
            const maxVal = Math.max(...trendData.map(d => Math.max(d.revenue, Math.abs(d.profit))), 1);
            const minVal = Math.min(...trendData.map(d => Math.min(d.revenue, d.profit)), 0);
            const range = maxVal - minVal || 1;
            const x = (i: number) => PAD + (i / (trendData.length - 1)) * (W - PAD * 2);
            const y = (v: number) => PAD + (1 - (v - minVal) / range) * (H - PAD * 2);
            const revPoints = trendData.map((d, i) => `${x(i)},${y(d.revenue)}`).join(' ');
            const profitPoints = trendData.map((d, i) => `${x(i)},${y(d.profit)}`).join(' ');
            const revArea = `${x(0)},${y(0)} ${revPoints} ${x(trendData.length - 1)},${y(0)}`;
            const zeroY = y(0);
            return (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Revenue & Profit Trend ({filters.startDate} – {filters.endDate})</h3>
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" preserveAspectRatio="none">
                {/* Zero line */}
                <line x1={PAD} y1={zeroY} x2={W - PAD} y2={zeroY} stroke="#e5e7eb" strokeWidth="0.5" />
                {/* Revenue area fill */}
                <polygon points={revArea} fill="rgba(147,197,253,0.3)" />
                {/* Revenue line */}
                <polyline points={revPoints} fill="none" stroke="#3b82f6" strokeWidth="1.5" />
                {/* Profit line */}
                <polyline points={profitPoints} fill="none" stroke="#22c55e" strokeWidth="1.5" />
              </svg>
              <div className="flex justify-between mt-1 text-[10px] text-gray-400">
                <span>{trendData[0]?.date?.slice(5)}</span>
                <span className="flex gap-3">
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-blue-400" /> Revenue</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-green-400" /> Profit</span>
                </span>
                <span>{trendData[trendData.length - 1]?.date?.slice(5)}</span>
              </div>
              {/* TACoS trend line */}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-gray-500">TACoS:</span>
                {trendData.slice(-7).map((d, i) => (
                  <span key={i} className={`text-[10px] font-medium ${d.tacos > 15 ? 'text-red-500' : d.tacos > 10 ? 'text-yellow-500' : 'text-green-500'}`}>
                    {d.tacos.toFixed(0)}%
                  </span>
                ))}
                <span className="text-[10px] text-gray-400 ml-1">(last 7 days)</span>
              </div>
            </div>
          ); })()}

          {/* KPI Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
            <KpiCard label="Revenue" value={fmt(k.revenue)} />
            <KpiCard label="Profit" value={fmt(k.cm2)} sub={`${k.cm2Pct.toFixed(1)}% margin`}
              color={k.cm2Pct >= 15 ? 'green' : k.cm2Pct >= 0 ? 'yellow' : 'red'} />
            <KpiCard label="COGS" value={fmt(k.cogs)} />
            <KpiCard label="Amazon Fees" value={fmt(k.totalFees)} />
            <KpiCard label="Ad Spend" value={fmt(k.adSpend)} />
            {beAcos !== null && (
              <KpiCard label="BE ACoS" value={`${beAcos.toFixed(0)}%`}
                sub="max before loss" color="blue" />
            )}
            <KpiCard label="TACoS" value={`${k.tacos.toFixed(1)}%`} />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button onClick={handleBackfill} disabled={backfilling}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
              {backfilling ? 'Recalculating...' : 'Recalculate P&L'}
            </button>
            {backfillMsg && <span className="self-center text-xs text-green-600">{backfillMsg}</span>}
          </div>

          {/* Daily P&L Table */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-gray-700">Daily P&L</h3>
            {dailyAggregated.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500">No daily data available for this period.</p>
            ) : (
              <div className="mt-4 overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b border-gray-200">
                      <th className="pb-2 pt-1 text-left text-xs font-medium text-gray-500">Date</th>
                      <th className="pb-2 pt-1 text-right text-xs font-medium text-gray-500">Revenue</th>
                      <th className="pb-2 pt-1 text-right text-xs font-medium text-gray-500">COGS</th>
                      <th className="pb-2 pt-1 text-right text-xs font-medium text-gray-500">Amz Fees</th>
                      <th className="pb-2 pt-1 text-right text-xs font-medium text-gray-500">PPC</th>
                      <th className="pb-2 pt-1 text-right text-xs font-medium text-gray-500">Promos</th>
                      <th className="pb-2 pt-1 text-right text-xs font-medium text-gray-500">Refunds</th>
                      <th className="pb-2 pt-1 text-right text-xs font-medium text-gray-500">Profit</th>
                      <th className="pb-2 pt-1 text-right text-xs font-medium text-gray-500">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {dailyAggregated.map(row => {
                      const fees = row.referralFee + row.fbaFee + row.storageFee + row.otherFees + row.inboundFee + row.returnsFee;
                      const margin = row.revenue > 0 ? (row.netProfit / row.revenue) * 100 : 0;
                      return (
                        <tr key={row.date}>
                          <td className="py-2 text-gray-700">{row.date}</td>
                          <td className="py-2 text-right font-medium text-gray-900">{fmt(row.revenue)}</td>
                          <td className="py-2 text-right text-gray-500">{fmt(row.cogs)}</td>
                          <td className="py-2 text-right text-gray-500">{fmt(fees)}</td>
                          <td className="py-2 text-right text-gray-500">{fmt(row.adSpend)}</td>
                          <td className="py-2 text-right text-gray-500">{row.promotions > 0 ? fmt(row.promotions) : '—'}</td>
                          <td className="py-2 text-right text-gray-500">{row.refunds > 0 ? fmt(row.refunds) : '—'}</td>
                          <td className={`py-2 text-right font-medium ${row.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {fmt(row.netProfit)}
                          </td>
                          <td className={`py-2 text-right text-xs font-medium ${margin >= 15 ? 'text-green-600' : margin >= 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {margin.toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Top/Bottom SKUs + Marketplace */}
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h3 className="text-sm font-semibold text-green-700">Top 5 SKUs by Profit</h3>
              <SkuTable skus={data.topSkus} displayName={displayName} parentNickname={parentNickname} fmt={fmt} />
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h3 className="text-sm font-semibold text-red-700">Bottom 5 SKUs by Profit</h3>
              <SkuTable skus={data.bottomSkus} displayName={displayName} parentNickname={parentNickname} fmt={fmt} />
            </div>
          </div>

          {data.marketplaceBreakdown.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h3 className="text-sm font-semibold text-gray-700">Marketplace Breakdown</h3>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="pb-2 text-left text-xs font-medium text-gray-500">Market</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Revenue</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Fees</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Ad Spend</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Net Profit</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.marketplaceBreakdown.map(m => {
                      const margin = m.revenue > 0 ? (m.netProfit / m.revenue * 100) : 0;
                      return (
                        <tr key={m.marketplace}>
                          <td className="py-2 font-medium text-gray-900">{MKT_LABELS[m.marketplace] ?? m.marketplace}</td>
                          <td className="py-2 text-right text-gray-700">{fmt(m.revenue)}</td>
                          <td className="py-2 text-right text-gray-500">{fmt(m.fees)}</td>
                          <td className="py-2 text-right text-gray-500">{fmt(m.adSpend)}</td>
                          <td className={`py-2 text-right font-medium ${m.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(m.netProfit)}</td>
                          <td className={`py-2 text-right text-xs font-medium ${margin >= 15 ? 'text-green-600' : margin >= 0 ? 'text-yellow-600' : 'text-red-600'}`}>{margin.toFixed(1)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
        );
      })()}
    </div>
  );
}

// ── CM Waterfall Row ─────────────────────────────────────────────────────────

function WaterfallRow({ label, value, total, color, indent, isTotal, isSubtotal, pct, badge, fmt }: {
  label: string; value: number; total: number; color: string;
  indent?: boolean; isTotal?: boolean; isSubtotal?: boolean;
  pct?: number; badge?: string; fmt?: (v: number) => string;
}) {
  const barWidth = total > 0 ? Math.min(Math.abs(value / total) * 100, 100) : 0;
  const colorMap: Record<string, { bg: string; text: string }> = {
    blue: { bg: 'bg-blue-500', text: 'text-blue-700' },
    green: { bg: 'bg-green-500', text: 'text-green-700' },
    red: { bg: 'bg-red-400', text: 'text-red-600' },
    orange: { bg: 'bg-orange-400', text: 'text-orange-600' },
  };
  const c = colorMap[color] ?? colorMap.blue;

  return (
    <div className={`flex items-center gap-3 ${isSubtotal ? 'py-1.5 border-t border-gray-200' : 'py-0.5'} ${indent ? 'pl-4' : ''}`}>
      <div className={`w-36 text-xs ${isTotal || isSubtotal ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>
        {label}
      </div>
      <div className="flex-1">
        <div className="h-4 w-full rounded-full bg-gray-100 overflow-hidden">
          <div className={`h-full rounded-full ${c.bg} transition-all`} style={{ width: `${barWidth}%` }} />
        </div>
      </div>
      <div className={`w-24 text-right text-xs font-medium ${c.text}`}>
        {fmt ? fmt(value) : `${value >= 0 ? '' : '-'}$${Math.abs(value).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`}
      </div>
      {pct !== undefined && (
        <span className={`w-12 text-right text-[10px] font-medium ${c.text}`}>{pct.toFixed(1)}%</span>
      )}
      {badge && (
        <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">{badge}</span>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  const textColor = color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' :
    color === 'yellow' ? 'text-yellow-600' : color === 'blue' ? 'text-blue-600' : 'text-gray-900';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-lg font-bold ${textColor}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-gray-400">{sub}</p>}
    </div>
  );
}

function SkuTable({ skus, displayName, parentNickname, fmt }: { skus: SkuSummary[]; displayName: (sku: string) => string; parentNickname: (sku: string) => string | null; fmt: (v: number) => string }) {
  if (skus.length === 0) return <p className="mt-4 text-sm text-gray-500">No SKU data available.</p>;
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-gray-100">
          <th className="pb-2 text-left text-xs font-medium text-gray-500">SKU</th>
          <th className="pb-2 text-right text-xs font-medium text-gray-500">Revenue</th>
          <th className="pb-2 text-right text-xs font-medium text-gray-500">Net Profit</th>
          <th className="pb-2 text-right text-xs font-medium text-gray-500">Units</th>
        </tr></thead>
        <tbody className="divide-y divide-gray-50">
          {skus.map(s => (
            <tr key={s.sku}>
              <td className="py-2 max-w-[200px] font-medium text-gray-900" title={s.sku}>
                {parentNickname(s.sku) && <span className="text-[10px] text-gray-400 block font-normal">{parentNickname(s.sku)}</span>}
                {displayName(s.sku)}
              </td>
              <td className="py-2 text-right text-gray-700">{fmt(s.revenue)}</td>
              <td className={`py-2 text-right font-medium ${s.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(s.netProfit)}</td>
              <td className="py-2 text-right text-gray-500">{s.units}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-48 rounded bg-gray-200" />
      <div className="h-64 rounded-xl bg-gray-200" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-20 rounded-xl bg-gray-200" />)}
      </div>
    </div>
  );
}

type AggDay = DailyRow;

function aggregateByDate(rows: DailyRow[]): AggDay[] {
  const map = new Map<string, AggDay>();
  for (const row of rows) {
    const ex = map.get(row.date) ?? {
      date: row.date, revenue: 0, cogs: 0,
      referralFee: 0, fbaFee: 0, storageFee: 0, otherFees: 0,
      inboundFee: 0, returnsFee: 0,
      adSpend: 0, netProfit: 0, marginPct: 0,
      refunds: 0, promotions: 0, reimbursements: 0, units: 0,
    };
    ex.revenue += row.revenue; ex.cogs += row.cogs;
    ex.referralFee += row.referralFee; ex.fbaFee += row.fbaFee;
    ex.storageFee += row.storageFee; ex.otherFees += row.otherFees;
    ex.inboundFee += row.inboundFee; ex.returnsFee += row.returnsFee;
    ex.adSpend += row.adSpend; ex.netProfit += row.netProfit;
    ex.refunds += row.refunds; ex.promotions += row.promotions;
    ex.reimbursements += row.reimbursements; ex.units += row.units;
    ex.marginPct = ex.revenue > 0 ? (ex.netProfit / ex.revenue) * 100 : 0;
    map.set(row.date, ex);
  }
  return Array.from(map.values()).sort((a, b) => b.date.localeCompare(a.date));
}
